import * as React from "react";
import { Command as Cmdk } from "cmdk";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";

export const Command = React.forwardRef<React.ComponentRef<typeof Cmdk>, React.ComponentPropsWithoutRef<typeof Cmdk>>(
  function Command({ className, ...props }, ref) {
    return <Cmdk ref={ref} className={cn("flex h-full w-full flex-col overflow-hidden text-popover-foreground", className)} {...props} />;
  },
);

export const CommandInput = React.forwardRef<
  React.ComponentRef<typeof Cmdk.Input>,
  React.ComponentPropsWithoutRef<typeof Cmdk.Input> & { icon?: boolean }
>(function CommandInput({ className, icon = true, ...props }, ref) {
  return (
    <div className="flex items-center gap-2 border-b border-border px-3">
      {icon && <Search className="size-4 shrink-0 text-subtle" />}
      <Cmdk.Input
        ref={ref}
        className={cn("h-11 w-full bg-transparent text-[14.5px] outline-none placeholder:text-subtle", className)}
        {...props}
      />
    </div>
  );
});

export function CommandList({ className, ...props }: React.ComponentPropsWithoutRef<typeof Cmdk.List>) {
  return <Cmdk.List className={cn("max-h-[360px] overflow-y-auto overscroll-contain p-1", className)} {...props} />;
}

export function CommandEmpty({ className, ...props }: React.ComponentPropsWithoutRef<typeof Cmdk.Empty>) {
  return <Cmdk.Empty className={cn("px-3 py-6 text-center text-[13.5px] text-muted-foreground", className)} {...props} />;
}

export function CommandGroup({ className, ...props }: React.ComponentPropsWithoutRef<typeof Cmdk.Group>) {
  return (
    <Cmdk.Group
      className={cn(
        "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export const CommandItem = React.forwardRef<React.ComponentRef<typeof Cmdk.Item>, React.ComponentPropsWithoutRef<typeof Cmdk.Item>>(
  function CommandItem({ className, ...props }, ref) {
    return (
      <Cmdk.Item
        ref={ref}
        className={cn(
          "flex min-h-8 cursor-default select-none items-center gap-2 rounded-md px-2 py-1 text-[13.5px] outline-none data-[disabled=true]:opacity-45 data-[selected=true]:bg-hover [&_svg]:size-4 [&_svg]:shrink-0 [&_svg:not([data-tone])]:text-muted-foreground",
          className,
        )}
        {...props}
      />
    );
  },
);

export function CommandSeparator({ className }: { className?: string }) {
  return <Cmdk.Separator className={cn("-mx-1 my-1 h-px bg-border", className)} />;
}
